const escapeHtmlText = (s: string) =>
  s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");

const formatInline = (text: string): string => {
  let escaped = escapeHtmlText(text);
  escaped = escaped.replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>");
  escaped = escaped.replace(/\*(.+?)\*/g, "<em>$1</em>");
  return escaped;
};

type FormatOptions = { compact?: boolean };

/** Mirrors backend email formatting: **bold**, *italic*, # / ## headings, line breaks. */
export const formatRichTextToHtml = (
  raw: string,
  options?: FormatOptions,
): string => {
  if (!raw) return "";

  const compact = options?.compact ?? false;
  const normalized = String(raw).replace(/<br\s*\/?>/gi, "\n");
  const lines = normalized.split("\n");
  const blocks: string[] = [];
  let paragraphLines: string[] = [];

  const flushParagraph = () => {
    if (paragraphLines.length === 0) return;
    const inner = paragraphLines.map(formatInline).join("<br>");
    const cls = compact
      ? "mb-1 last:mb-0 leading-snug"
      : "mb-2 last:mb-0 leading-relaxed";
    blocks.push(`<p class="${cls}">${inner}</p>`);
    paragraphLines = [];
  };

  for (const line of lines) {
    const trimmed = line.trim();
    if (!trimmed) {
      flushParagraph();
      continue;
    }

    if (trimmed.startsWith("## ")) {
      flushParagraph();
      const text = formatInline(trimmed.slice(3));
      if (compact) {
        blocks.push(`<p class="font-semibold mb-1 leading-snug">${text}</p>`);
      } else {
        blocks.push(
          `<h2 class="text-base font-bold mt-2 mb-1 first:mt-0">${text}</h2>`,
        );
      }
      continue;
    }

    if (trimmed.startsWith("# ")) {
      flushParagraph();
      const text = formatInline(trimmed.slice(2));
      if (compact) {
        blocks.push(`<p class="font-semibold mb-1 leading-snug">${text}</p>`);
      } else {
        blocks.push(
          `<h1 class="text-lg font-bold mt-2 mb-1 first:mt-0">${text}</h1>`,
        );
      }
      continue;
    }

    if (trimmed.startsWith("- ")) {
      flushParagraph();
      blocks.push(
        `<div class="flex gap-2 mb-1 leading-relaxed"><span class="shrink-0">•</span><span>${formatInline(trimmed.slice(2))}</span></div>`,
      );
      continue;
    }

    paragraphLines.push(line);
  }

  flushParagraph();
  return blocks.join("");
};

/**
 * Single-block HTML for card previews (line-clamp safe).
 * Keeps line breaks, bold, italic, headings and bullets — no nested block elements.
 */
export const formatRichTextToCardPreview = (raw: string): string => {
  if (!raw) return "";

  const normalized = String(raw).replace(/<br\s*\/?>/gi, "\n");
  const segments: string[] = [];

  for (const line of normalized.split("\n")) {
    const trimmed = line.trim();
    if (!trimmed) {
      segments.push("");
      continue;
    }
    if (trimmed.startsWith("## ")) {
      segments.push(`<strong>${formatInline(trimmed.slice(3))}</strong>`);
      continue;
    }
    if (trimmed.startsWith("# ")) {
      segments.push(`<strong>${formatInline(trimmed.slice(2))}</strong>`);
      continue;
    }
    if (trimmed.startsWith("- ")) {
      segments.push(`• ${formatInline(trimmed.slice(2))}`);
      continue;
    }
    segments.push(formatInline(trimmed));
  }

  return segments.join("<br>");
};

/** Strip markdown/light HTML to a single line of plain text (for previews). */
export const richTextToPlain = (raw: string): string => {
  if (!raw) return "";
  return String(raw)
    .replace(/<br\s*\/?>/gi, "\n")
    .replace(/^#{1,2}\s+/gm, "")
    .replace(/^-\s+/gm, "")
    .replace(/\*\*(.+?)\*\*/g, "$1")
    .replace(/\*(.+?)\*/g, "$1")
    .replace(/\s+/g, " ")
    .trim();
};
